import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, ArrowRight, Send, Loader2 } from 'lucide-react';
import { apiInvoices, invoiceActions } from '../../lib/api';
import { fmt, invoicePaid, invoiceOutstanding } from '../../lib/invoiceItems';
import { toast } from 'sonner';

export default function InvoicesQuickView() {
  const navigate = useNavigate();
  const [inv, setInv] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => { apiInvoices.list().then((d) => setInv(d || [])).catch(() => setInv([])); }, []);
  const num = (x) => Number(x) || 0;
  const open = inv.filter((i) => String(i.status).toLowerCase() !== 'paid').reduce((a, i) => a + invoiceOutstanding(i), 0);
  const overdue = inv.filter((i) => i.status === 'Overdue').reduce((a, i) => a + invoiceOutstanding(i), 0);
  const paid = inv.reduce((a, i) => a + invoicePaid(i), 0);
  const unpaidCount = inv.filter((i) => String(i.status).toLowerCase() !== 'paid').length;

  const sendPayLinks = async (onlyOverdue) => {
    const label = onlyOverdue ? 'overdue' : 'unpaid';
    if (!window.confirm(`Email a payable invoice (with a Pay Online link) to every ${label} customer who has an email on file?`)) return;
    setSending(true);
    try {
      const r = await invoiceActions.sendBulk({ onlyOverdue });
      toast.success(`Sent ${r.sent} pay link${r.sent === 1 ? '' : 's'} · ${r.skipped} skipped · ${r.failed} failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send (is email configured?)');
    } finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="invoices-quickview">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Invoices</h1>
          <p className="text-slate-500 mt-1">{inv.length} invoices · manage all billing inside Accounting.</p>
        </div>
        <Button onClick={() => navigate('/app/accounting/invoices')} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-2">
          Open in Accounting <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200"><div className="text-sm text-slate-500">Open</div><div className="text-3xl font-extrabold text-slate-900 mt-1">{fmt(open)}</div></Card>
        <Card className="p-5 border-slate-200"><div className="text-sm text-slate-500">Overdue</div><div className="text-3xl font-extrabold text-red-600 mt-1">{fmt(overdue)}</div></Card>
        <Card className="p-5 border-slate-200"><div className="text-sm text-slate-500">Paid</div><div className="text-3xl font-extrabold text-emerald-600 mt-1">{fmt(paid)}</div></Card>
      </div>

      <Card className="p-6 border-[#8BC53F]/40 bg-[#8BC53F]/5" data-testid="bulk-paylink-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-bold text-slate-900"><Send className="w-4 h-4 text-[#6FA82C]" />Get paid faster — send Pay Links</div>
            <p className="text-sm text-slate-600 mt-1">Email every customer a one-tap Pay Online link for their unpaid invoice. {unpaidCount} unpaid on file.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={sending} onClick={() => sendPayLinks(true)} className="gap-1.5" data-testid="send-overdue-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Overdue only</Button>
            <Button disabled={sending} onClick={() => sendPayLinks(false)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="send-all-unpaid-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}All unpaid</Button>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-10 text-center">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h3 className="font-bold text-slate-900">Full invoice tools live in Accounting</h3>
        <p className="text-slate-500 text-sm mt-1">Create, send, charge cards, and track invoices with the built-in Accounting module.</p>
        <Button onClick={() => navigate('/app/accounting/invoices')} className="mt-5 bg-[#8BC53F] hover:bg-[#6FA82C]">Go to Invoices</Button>
      </Card>
    </div>
  );
}
